import React, { memo, useMemo } from 'react';

import { AGILITY_BACKLOG } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { ForgeActionMenuItems as BaseForgeActionMenuItems } from '@atlassian/jira-forge-ui-action-apps/src/ui/action-menu/index.tsx';
import { SPRINT_ACTION_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { SprintExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import { forgeSprintModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useBacklogSelector } from '../../../../state/index.tsx';
import { getBacklogExtensionData } from '../../../../state/selectors/app/index.tsx';

type ForgeSprintActionMenuItemsProps = {
	[K in keyof SprintExtensionData as `sprint${Capitalize<K>}`]: SprintExtensionData[K];
};

const ForgeActionMenuItems = ({ sprintId, sprintState }: ForgeSprintActionMenuItemsProps) => {
	const { loading, data: extensions } = useResource(forgeSprintModuleResource);
	const backlogExtensionData = useBacklogSelector(getBacklogExtensionData);

	const memoedExtensionData = useMemo(
		() => ({
			...backlogExtensionData,
			sprint: {
				id: sprintId,
				state: sprintState,
			},
		}),
		[backlogExtensionData, sprintId, sprintState],
	);

	if (loading || !extensions || extensions.length === 0) {
		return null;
	}

	return (
		<BaseForgeActionMenuItems
			module={SPRINT_ACTION_MODULE}
			analyticsSource={AGILITY_BACKLOG}
			testId="software-backlog.card-list.sprints-menu.forge-items"
			extensions={extensions}
			extensionData={memoedExtensionData}
		/>
	);
};

export const ForgeSprintActionMenuItems = memo((props: ForgeSprintActionMenuItemsProps) => (
	<ErrorBoundary packageName="software" id="forge-items">
		<ForgeActionMenuItems {...props} />
	</ErrorBoundary>
));
